import React, { useState, useEffect, useRef } from 'react';
import { Grid, Row } from 'react-bootstrap';
import { Textfit } from 'react-textfit';
import TimeMaster from './timeMaster';
import EventSystem from './EventSystem';
import GlobalFunction from './globalFunction';
import { RaisedPanel } from './style/tile.style';
import './css/clockModule.css';

export default function ClockModule({ parameters, onSendMessage, mode }) {
    const eventId = parameters.config.event_id;
    const DEFAULT_OTHER_CONFIG = { topColour: '', topBackground: '', middleColour: '', middleBackground: '', bottomColour: '', bottomBackground: '', errorCount: 0 };

    const [otherConfig, setOtherConfig] = useState(() => {
        const oc = parameters.config.other_config;
        if (!Object.is(oc, null) && Object.entries(oc).length === 7) return oc;
        return DEFAULT_OTHER_CONFIG;
    });
    const [clockLocalOffset, setClockLocalOffset] = useState(() => {
        if (parameters.config.currentTimeMaster === 'ME') return 0;
        const v = localStorage.getItem('clockLocalOffset_' + eventId);
        return v === null ? 0 : parseFloat(v);
    });
    const [broadcastDelay, setBroadcastDelay] = useState(() => {
        const v = localStorage.getItem('broadcastDelay_' + eventId);
        return v === null ? 0 : parseFloat(v);
    });
    const [inDelay, setInDelay] = useState(() => {
        const v = localStorage.getItem('inDelay_' + eventId);
        return v === null ? true : JSON.parse(v);
    });
    const [showTimeMaster, setShowTimeMaster] = useState(false);
    const [timeInWords, setTimeInWords] = useState('');
    const [burgerFontSize, setBurgerFontSize] = useState(0);
    const [bottomBunFontSize, setBottomBunFontSize] = useState(0);
    const [numberWidth, setNumberWidth] = useState(0);
    const [clockWidth, setClockWidth] = useState(0);
    const [currentTime, setCurrentTime] = useState({ hours: 0, minutes: 0, seconds: 0, meridian: '' });
    const [textDate, setTextDate] = useState('');

    const containerRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const fontSizeCheckerWrapRef = useRef(null);
    const liveRef = useRef({ burgerFontSize: 0, timeInWords: '', containerHeight: 0 });

    liveRef.current.containerHeight = parameters.containerHeight.container;

    function updateDimensions() {
        const bunHeight = Math.floor((containerRef.current.clientHeight - 2) / 4);
        const bunWidth = containerRef.current.clientWidth - 22;
        const fs = GlobalFunction.getFontSize('00:00:00 am', bunWidth, bunHeight * 0.9, fontSizeCheckerRef.current);
        setBottomBunFontSize(fs);
        setClockWidth(bunWidth);
        setNumberWidth(GlobalFunction.getTextWidth('00', fs, fontSizeCheckerRef.current));
    }

    useEffect(() => {
        updateDimensions();
        EventSystem.subscribe('ClockModule', respondToMessage);
        window.addEventListener('resize', updateDimensions);

        if (mode !== 'setup') {
            EventSystem.subscribe('1000msHeartbeat', refreshClock);
            const success = EventSystem.publish('localClockOffset', clockLocalOffset);
            if (!success) {
                setTimeout(() => EventSystem.publish('localClockOffset', clockLocalOffset), 500);
            }
        }

        return () => {
            EventSystem.unsubscribe('1000msHeartbeat', refreshClock);
            EventSystem.unsubscribe('ClockModule', respondToMessage);
            window.removeEventListener('resize', updateDimensions);
        };
    }, []);

    useEffect(() => {
        if (containerRef.current) updateDimensions();
    }, [parameters.containerHeight.container]);

    // CWRP: reset offset when we become time master
    const firstCWRPRender = useRef(true);
    useEffect(() => {
        if (firstCWRPRender.current) { firstCWRPRender.current = false; return; }
        if (parameters.config.currentTimeMaster === 'ME') {
            setClockLocalOffset(0);
        }
    }, [parameters.config.currentTimeMaster]);

    function refreshClock(updatedClockInfo) {
        let fontSize = liveRef.current.burgerFontSize;
        if (updatedClockInfo.timeInWords !== liveRef.current.timeInWords) {
            fontSize = GlobalFunction.getMultiLineFontSize(
                toTitleCase(updatedClockInfo.timeInWords),
                containerRef.current.clientWidth - 22,
                Math.floor((liveRef.current.containerHeight - 12) / 2),
                fontSizeCheckerWrapRef.current
            );
        }
        const titleCased = toTitleCase(updatedClockInfo.timeInWords);
        liveRef.current.burgerFontSize = fontSize;
        liveRef.current.timeInWords = titleCased;
        setTimeInWords(titleCased);
        setBurgerFontSize(fontSize);
        setCurrentTime(updatedClockInfo.currentTime);
        setTextDate(updatedClockInfo.textDate);
    }

    function respondToMessage(data) {
        if (data.action === 'set_delay') {
            const stored = localStorage.getItem('inDelay_' + eventId);
            setInDelay(stored === null ? true : JSON.parse(stored));
        }
    }

    function toggleDelay() {
        const newInDelay = !inDelay;
        setInDelay(newInDelay);
        localStorage.setItem('inDelay_' + eventId, JSON.stringify(newInDelay));
        const msg = {
            sourceModuleName: 'ClockModule',
            event_grouping_seq: 0,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: eventId,
            action: 'set_delay',
            delay: 0,
        };
        if (newInDelay) {
            const stored = localStorage.getItem('broadcastDelay_' + eventId);
            if (stored !== null) msg.delay = parseFloat(stored);
        }
        onSendMessage(msg);
    }

    const bunHeight = Math.floor((parameters.containerHeight.container - 12) / 4);
    const burgerHeight = Math.floor((parameters.containerHeight.container - 12) / 2);
    const timeMasterBottom = (bunHeight / 2) + 5;

    const burgerColor = otherConfig.middleColour.length > 0 ? otherConfig.middleColour : GlobalFunction.getPaletteColour(parameters.config.colour);
    const burgerBackground = otherConfig.middleBackground;
    const currentTimeColour = otherConfig.topColour.length > 0 ? otherConfig.topColour : GlobalFunction.getPaletteColour(parameters.config.colour) + '66';
    const currentTimeBackgroundColour = otherConfig.topBackground;
    const bottomTimeColour = otherConfig.bottomColour.length > 0 ? otherConfig.bottomColour : GlobalFunction.getPaletteColour(parameters.config.colour) + '66';
    const bottomTimeBackgroundColour = otherConfig.bottomBackground;

    const burgerStyle = { height: burgerHeight + 'px', fontSize: burgerFontSize + 'px', lineHeight: burgerFontSize + 'px', paddingLeft: '5px', paddingRight: '5px', marginLeft: 0, marginRight: 0, textAlign: 'center', color: burgerColor, backgroundColor: burgerBackground };
    const bunStyle = { height: bunHeight + 'px', lineHeight: bunHeight + 'px', marginLeft: 0, marginRight: 0 };
    const bottomBunStyle = { height: bunHeight + 'px', lineHeight: bunHeight + 'px', fontSize: bottomBunFontSize + 'px', whiteSpace: 'nowrap', marginLeft: '0', marginRight: '0', paddingLeft: '0', paddingRight: '0' };
    const currentTimeStyle = { display: mode === 'setup' ? 'none' : 'block', paddingLeft: '10px', paddingRight: '10px', backgroundColor: currentTimeBackgroundColour, textAlign: 'center', color: currentTimeColour };
    const bottomTimeStyle = { display: mode === 'setup' ? 'none' : 'block', textAlign: 'center', paddingLeft: '0px', paddingRight: '0px', backgroundColor: bottomTimeBackgroundColour, color: bottomTimeColour };
    const showOffsetStyle = { display: mode === 'setup' ? 'flex' : 'none', color: '#ffbe76', position: 'absolute', top: 0, left: 0 };
    let toggleHeight = (bunHeight * 0.9) < 25 ? bunHeight * 0.9 : 25;

    return (
        <RaisedPanel ref={containerRef} height={parameters.containerHeight.container - 3 + 'px'} style={{ overflow: 'hidden' }}>
            {showTimeMaster && (
                <TimeMaster
                    parameters={parameters}
                    onSendMessage={onSendMessage}
                    onExit={() => setShowTimeMaster(false)} />
            )}

            <Grid fluid={true} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <span style={{ height: '5px', width: '5px', borderRadius: '50%', color: '#ec0b43', display: parameters.config.currentTimeMaster === 'ME' && mode !== 'setup' ? 'block' : 'none', position: 'absolute', bottom: timeMasterBottom + 'px', left: '5px', zIndex: 1 }}>
                    &trade;
                </span>
                <div style={{ display: parameters.config.currentTimeMaster === 'ME' && mode !== 'setup' && broadcastDelay > 0 ? 'block' : 'none' }}>
                    <img src={inDelay ? 'staticImages/InDelay.svg' : 'staticImages/OutDelay.svg'}
                        height={toggleHeight} width={toggleHeight} className="cog"
                        onClick={toggleDelay}
                        style={{ zIndex: 5, position: 'absolute', bottom: bunHeight * 0.2, right: bunHeight * 0.1 }} />
                </div>
                <Textfit mode="single" forceSingleModeWidth={false}>
                    <Row style={bunStyle}>
                        <span style={currentTimeStyle}>{textDate}</span>
                    </Row>
                </Textfit>
                <Row style={burgerStyle}>
                    <span style={{ verticalAlign: 'middle' }}>{timeInWords}</span>
                </Row>
                <Row style={bottomBunStyle}>
                    <div style={bottomTimeStyle}>
                        <span style={{ textAlign: 'right' }}>{currentTime.hours}</span>
                        :
                        <span style={{ width: numberWidth + 'px', display: 'inline-block' }}>{currentTime.minutes}</span>
                        :
                        <span style={{ width: numberWidth + 'px', display: 'inline-block', textAlign: 'left' }}>{currentTime.seconds}</span>
                        {currentTime.meridian}
                    </div>
                </Row>
            </Grid>

            <div style={showOffsetStyle}>
                <span style={{ marginTop: '2px' }}>Clock Configure{' '}</span>
                <button onClick={() => setShowTimeMaster(true)} style={{ marginLeft: '6px', borderRadius: '50px', paddingLeft: '10px', paddingRight: '10px', paddingTop: '1px', paddingBottom: '1px', lineHeight: '1.2', fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit', cursor: 'pointer' }}>Set</button>
            </div>

            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }} ref={fontSizeCheckerRef} />
            <div style={{ position: 'fixed', top: '350px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'wrap' }} ref={fontSizeCheckerWrapRef} />
        </RaisedPanel>
    );
}

function toTitleCase(str) {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}
